import { useCallback, useEffect, useState } from "react"

import {
  api,
  type CampoLidoNaTela,
  type ContratoComLeitura,
  type LeituraDeUmContrato,
  type LeituraDosAgentes,
} from "../api"
import { Alert, AlertDescription, AlertTitle } from "../componentes/Alert"
import { mensagemDeRecusa } from "../componentes/BlocoDeCampos"
import { Button } from "../componentes/Button"
import { Empty, EmptyContent, EmptyDescription, EmptyHeader, EmptyMedia, EmptyTitle } from "../componentes/Empty"
import * as Icones from "../componentes/Icones"
import { Numero, Numeros } from "../componentes/Numeros"
import { Bloco, CarregandoNaSecao, FalhaNaSecao } from "../componentes/Secao"
import { Selo } from "../componentes/Selo"
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../componentes/Sheet"
import { Acao, Column, TablePrincipal2, TablePrincipalFiltroGlobal } from "../componentes/TablePrincipal2"
import { dataHora, inteiro, maiusculaInicial, numeroComAno } from "../formato"
import { BOTAO_BRANCO, CABECALHO_DO_SHEET, MAIUSCULA_INLINE, TEXTO_DO_CADASTRO } from "../lib/classes"
import { cn } from "../lib/cn"

type AchadoDoQuadro = LeituraDeUmContrato["achados"][number]

const NOME_DA_CONFIANCA: Record<string, string> = { alta: "Alta", media: "Média", baixa: "Baixa" }

const NOME_DO_PROBLEMA: Record<string, string> = {
  anomalia_modelo_nao_preenchido: "Contrato assinado com o modelo em branco (prazo ou valor zerados)",
}

const CONTRATOS_POR_PAGINA = 20

const NUMERO_A_DIREITA = (valor: unknown) => <span className="tabular-nums">{inteiro(Number(valor))}</span>

export function LeituraDosContratos({ aoAbrirContrato }: { aoAbrirContrato?: (id: number) => void }) {
  const [leitura, definirLeitura] = useState<LeituraDosAgentes | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .agentes()
      .then(definirLeitura)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [])
  useEffect(carregar, [carregar])

  const problemas = Object.entries(leitura?.anomalias ?? {})

  return (
    <div className="flex flex-col divide-y divide-(--borda)">
      <Bloco
        titulo="Leitura campo a campo"
        dica="O que o sistema leu nos documentos dos contratos, somado por campo. Confira sempre contra o documento. Só registrou conta o que foi lido mas não preenchido no contrato."
      >
        {leitura === null ? (
          falha !== null ? (
            <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
          ) : (
            <CarregandoNaSecao />
          )
        ) : (
          <>
            <Numeros>
              <Numero rotulo="Contratos com o documento lido" valor={inteiro(leitura.lidos)} nota={`de ${inteiro(leitura.contratos)} contratos`} />
              <Numero rotulo="Ainda não lidos" valor={inteiro(leitura.sem_leitura)} nota="a conferir pelo fiscal" />
              <Numero
                rotulo="Problemas no documento"
                valor={inteiro(problemas.reduce((soma, [, n]) => soma + n, 0))}
                nota="para o fiscal levar ao processo"
              />
            </Numeros>
            <TablePrincipal2
              data={leitura.campos}
              paginacao={false}
              pageCount={1}
              currentPage={1}
              onPageChange={() => undefined}
              listrada
              mensagemVazia={
                <Empty className="py-8">
                  <EmptyHeader>
                    <EmptyMedia variant="icon">
                      <Icones.Leitura />
                    </EmptyMedia>
                    <EmptyTitle>Nenhum documento lido ainda</EmptyTitle>
                    <EmptyDescription>A leitura acontece depois de cada importação do ATHUS.</EmptyDescription>
                  </EmptyHeader>
                </Empty>
              }
            >
              <Column<CampoLidoNaTela>
                name="rotulo"
                label="Campo"
                enableSorting={false}
                render={(valor) => <span className={cn("font-medium", MAIUSCULA_INLINE)}>{String(valor)}</span>}
              />
              <Column<CampoLidoNaTela> name="achou" label="Encontrou" enableSorting={false} render={NUMERO_A_DIREITA} />
              <Column<CampoLidoNaTela> name="aplicou" label="Preencheu" enableSorting={false} render={NUMERO_A_DIREITA} />
              <Column<CampoLidoNaTela> name="so_registrou" label="Só registrou" enableSorting={false} render={NUMERO_A_DIREITA} />
              <Column<CampoLidoNaTela> name="alta" label="Confiança alta" enableSorting={false} render={NUMERO_A_DIREITA} />
              <Column<CampoLidoNaTela>
                name="motivo_mais_comum"
                label="Motivo mais comum de não preencher"
                enableSorting={false}
                render={(valor) => (valor === null ? "—" : <span className={cn("text-(--tinta-2)", MAIUSCULA_INLINE)}>{String(valor)}</span>)}
              />
            </TablePrincipal2>
          </>
        )}
      </Bloco>

      <QuadroPorContrato aoAbrirContrato={aoAbrirContrato} />

      {problemas.length > 0 && (
        <Bloco
          titulo="Problemas nos documentos"
          dica="Problemas no próprio documento, vistos na leitura, como o contrato assinado com o modelo em branco (&quot;00 (000) meses&quot;, &quot;R$ 00,00&quot;). Não é erro do sistema: leve ao processo."
        >
          <Alert variant="destructive">
            <Icones.Risco />
            <AlertTitle>Leve ao processo</AlertTitle>
            <AlertDescription>
              <ul className="m-0 flex list-none flex-col gap-1 p-0">
                {problemas.map(([chave, quantos]) => (
                  <li key={chave}>
                    <strong className="tabular-nums">{plural(quantos, "contrato", "contratos")}</strong>
                    {": "}
                    {NOME_DO_PROBLEMA[chave] ?? maiusculaInicial(chave)}
                  </li>
                ))}
              </ul>
            </AlertDescription>
          </Alert>
        </Bloco>
      )}
    </div>
  )
}

function plural(quantidade: number, singular: string, varios: string): string {
  return `${inteiro(quantidade)} ${quantidade === 1 ? singular : varios}`
}

export function QuadroPorContrato({ aoAbrirContrato }: { aoAbrirContrato?: (id: number) => void } = {}) {
  const [busca, definirBusca] = useState("")
  const [contratos, definirContratos] = useState<ContratoComLeitura[] | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)
  const [tentativa, definirTentativa] = useState(0)
  const [pagina, definirPagina] = useState(1)
  const [porPagina, definirPorPagina] = useState(CONTRATOS_POR_PAGINA)
  const [aberto, definirAberto] = useState<ContratoComLeitura | null>(null)

  useEffect(() => {
    let vivo = true
    const relogio = setTimeout(() => {
      definirFalha(null)
      api
        .contratosLidosPelosAgentes(busca)
        .then((lista) => {
          if (vivo) definirContratos(lista)
        })
        .catch((erro: unknown) => {
          if (vivo) definirFalha(mensagemDeRecusa(erro))
        })
    }, busca === "" ? 0 : 250)
    return () => {
      vivo = false
      clearTimeout(relogio)
    }
  }, [busca, tentativa])

  const lista = contratos ?? []
  const totalDePaginas = Math.max(1, Math.ceil(lista.length / porPagina))
  const paginaAtual = Math.min(pagina, totalDePaginas)
  const recorte = lista.slice((paginaAtual - 1) * porPagina, paginaAtual * porPagina)

  const vazio =
    busca.trim() !== "" ? (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Buscar />
          </EmptyMedia>
          <EmptyTitle>Nada encontrado</EmptyTitle>
          <EmptyDescription>{`Nenhum contrato com leitura casa com "${busca}".`}</EmptyDescription>
        </EmptyHeader>
        <EmptyContent>
          <Button type="button" variant="outline" size="sm" className={BOTAO_BRANCO} onClick={() => definirBusca("")}>
            Limpar a busca
          </Button>
        </EmptyContent>
      </Empty>
    ) : (
      <Empty className="py-8">
        <EmptyHeader>
          <EmptyMedia variant="icon">
            <Icones.Contrato />
          </EmptyMedia>
          <EmptyTitle>Nenhum contrato lido</EmptyTitle>
          <EmptyDescription>Nenhum contrato tem a leitura do documento registrada ainda.</EmptyDescription>
        </EmptyHeader>
      </Empty>
    )

  return (
    <Bloco
      titulo="Leitura contrato a contrato"
      dica="Clique no contrato para ver cada campo que o sistema leu no documento, com a frase de onde tirou. Na ficha do contrato, o link &quot;como foi lido&quot; mostra a mesma frase ao lado do campo."
    >
      {contratos === null ? (
        falha !== null ? (
          <FalhaNaSecao mensagem={falha} aoTentar={() => definirTentativa((n) => n + 1)} />
        ) : (
          <CarregandoNaSecao />
        )
      ) : (
        <TablePrincipal2
          data={recorte}
          pageCount={totalDePaginas}
          currentPage={paginaAtual}
          pageSize={porPagina}
          totalRegistros={lista.length}
          onPageChange={definirPagina}
          onPageSizeChange={(tamanho) => {
            definirPorPagina(tamanho)
            definirPagina(1)
          }}
          globalFilterValue={busca}
          onGlobalFilterChange={(valor) => {
            definirBusca(valor)
            definirPagina(1)
          }}
          listrada
          mensagemVazia={vazio}
          aoClicarNaLinha={definirAberto}
        >
          <TablePrincipalFiltroGlobal globalFilter={busca} setGlobalFilter={definirBusca} placeholder="Número do contrato ou objeto" />
          <Column<ContratoComLeitura>
            name="numero"
            label="Contrato"
            enableSorting={false}
            render={(_valor, contrato) => <span className="font-medium whitespace-nowrap">{numeroComAno(contrato.numero, contrato.ano)}</span>}
          />
          <Column<ContratoComLeitura>
            name="objeto"
            label="Objeto"
            enableSorting={false}
            render={(valor) => <span className={TEXTO_DO_CADASTRO}>{String(valor)}</span>}
          />
          <Column<ContratoComLeitura> name="achados" label="Campos lidos" enableSorting={false} render={NUMERO_A_DIREITA} />
          <Column<ContratoComLeitura> name="aplicados" label="Preenchidos" enableSorting={false} render={NUMERO_A_DIREITA} />
          <Column<ContratoComLeitura>
            name="extraido_em"
            label="Lido em"
            enableSorting={false}
            render={(valor) => (valor === null ? "—" : <span className="whitespace-nowrap">{dataHora(String(valor))}</span>)}
          />
          <Acao<ContratoComLeitura> label="Ver o quadro" icon={Icones.Leitura} onClick={definirAberto} />
          {aoAbrirContrato !== undefined && (
            <Acao<ContratoComLeitura> label="Abrir a ficha do contrato" icon={Icones.Contrato} onClick={(contrato) => aoAbrirContrato(contrato.contrato_id)} />
          )}
        </TablePrincipal2>
      )}

      <Sheet open={aberto !== null} onOpenChange={(aberta) => !aberta && definirAberto(null)}>
        <SheetContent
          aria-describedby={undefined}
          className="w-full gap-0 overflow-y-auto bg-card p-0 sm:max-w-3xl [&>button:last-child]:text-primary-foreground [&>button:last-child]:opacity-100"
        >
          <SheetHeader className={CABECALHO_DO_SHEET}>
            <SheetTitle className="text-primary-foreground">
              {aberto !== null ? `Leitura: ${numeroComAno(aberto.numero, aberto.ano)}` : "Leitura"}
            </SheetTitle>
          </SheetHeader>
          {aberto !== null && (
            <QuadroDoContrato
              key={aberto.contrato_id}
              contrato={aberto}
              aoFechar={() => definirAberto(null)}
              aoAbrirContrato={
                aoAbrirContrato === undefined
                  ? undefined
                  : () => {
                      definirAberto(null)
                      aoAbrirContrato(aberto.contrato_id)
                    }
              }
            />
          )}
        </SheetContent>
      </Sheet>
    </Bloco>
  )
}

function seloDaConfianca(confianca: string | null) {
  return confianca === "alta" ? "confianca_alta" : confianca === "media" ? "confianca_media" : "confianca_baixa"
}

function QuadroDoContrato({
  contrato,
  aoFechar,
  aoAbrirContrato,
}: {
  contrato: ContratoComLeitura
  aoFechar: () => void
  aoAbrirContrato?: () => void
}) {
  const [quadro, definirQuadro] = useState<LeituraDeUmContrato | null>(null)
  const [falha, definirFalha] = useState<string | null>(null)

  const carregar = useCallback(() => {
    definirFalha(null)
    api
      .leituraDoContrato(contrato.contrato_id)
      .then(definirQuadro)
      .catch((erro: unknown) => definirFalha(mensagemDeRecusa(erro)))
  }, [contrato.contrato_id])
  useEffect(carregar, [carregar])

  return (
    <div className="flex flex-col gap-4 px-6 pt-3 pb-6">
      <div className="flex flex-col gap-0.5 text-sm">
        <span className={cn("font-medium text-(--tinta)", TEXTO_DO_CADASTRO)}>{contrato.objeto}</span>
        {(quadro?.extraido_em ?? contrato.extraido_em) !== null && (
          <span className="text-(--tinta-2)">{`Lido em ${dataHora(String(quadro?.extraido_em ?? contrato.extraido_em))}`}</span>
        )}
      </div>

      {quadro === null ? (
        falha !== null ? (
          <FalhaNaSecao mensagem={falha} aoTentar={carregar} />
        ) : (
          <CarregandoNaSecao />
        )
      ) : (
        <>
          <TablePrincipal2
            data={quadro.achados}
            paginacao={false}
            pageCount={1}
            currentPage={1}
            onPageChange={() => undefined}
            listrada
            mensagemVazia="O sistema não conseguiu ler nada deste documento."
          >
            <Column<AchadoDoQuadro> name="rotulo" label="Campo" enableSorting={false} render={(valor) => <span className="font-medium">{String(valor)}</span>} />
            <Column<AchadoDoQuadro> name="valor" label="Valor" enableSorting={false} render={(valor) => (valor === null ? "—" : String(valor))} />
            <Column<AchadoDoQuadro>
              name="confianca"
              label="Confiança"
              enableSorting={false}
              render={(_valor, achado) => (
                <Selo situacao={seloDaConfianca(achado.confianca)}>
                  {achado.confianca === null ? "Sem confiança declarada" : (NOME_DA_CONFIANCA[achado.confianca] ?? maiusculaInicial(achado.confianca))}
                </Selo>
              )}
            />
            <Column<AchadoDoQuadro>
              name="aplicado"
              label="Situação"
              enableSorting={false}
              render={(_valor, achado) => (
                <Selo situacao={achado.aplicado ? "preenchido" : "so_registrado"} titulo={achado.motivo ?? undefined}>
                  {achado.aplicado ? "Preenchido" : "Só registrado"}
                </Selo>
              )}
            />
            <Column<AchadoDoQuadro>
              name="evidencia"
              label="De onde tirou"
              enableSorting={false}
              render={(_valor, achado) => (
                <span className="flex flex-col gap-1">
                  <span className={MAIUSCULA_INLINE}>{achado.evidencia ?? "—"}</span>
                  {achado.motivo !== null && (
                    <span className={cn("text-xs text-(--tinta-2)", MAIUSCULA_INLINE)}>{`Não preenchido porque ${achado.motivo}`}</span>
                  )}
                </span>
              )}
            />
          </TablePrincipal2>

          {quadro.nao_achados.length > 0 && (
            <Alert>
              <Icones.Informacao />
              <AlertTitle>O sistema não encontrou</AlertTitle>
              <AlertDescription>{`${maiusculaInicial(quadro.nao_achados.join(", "))}. Confira esses campos no documento.`}</AlertDescription>
            </Alert>
          )}
        </>
      )}

      <div className="mt-1 flex justify-end gap-2 border-t border-(--borda) pt-4">
        <Button type="button" variant="outline" onClick={aoFechar}>
          <Icones.Fechar />
          Fechar
        </Button>
        {aoAbrirContrato !== undefined && (
          <Button type="button" onClick={aoAbrirContrato}>
            <Icones.Abrir />
            Abrir a ficha do contrato
          </Button>
        )}
      </div>
    </div>
  )
}
